import React, { useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Card,
  Typography,
  Button,
  Descriptions,
  Row,
  Col,
  Space,
  Spin,
  Tabs,
  Tag,
  message,
} from 'antd';
import {
  ArrowLeftOutlined,
  EditOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { Visitor, VisitorStatus, VisitorType } from '@/types/residents/visitor';
import { getVisitorById, updateVisitor, deleteVisitor } from '@/api/residents';
import { useAuth } from '@/contexts/AuthContext';
import ActivityLogList from '@/components/common/ActivityLogList';
import DeleteConfirmationModal from '@/components/common/DeleteConfirmationModal';
import VisitorModal from '../../components/features/residents/VisitorModal';
import { ResidentTab, EmployeeTab } from '@/components/tab';

const { Title, Text } = Typography;


const VisitorDetailsPage: React.FC = () => {
  const { t } = useTranslation(['visitors', 'common']);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { firestoreUser } = useAuth();
  
  const state = location.state as { visitor: Visitor } | null;

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  // Query - CRITICAL: Only fetch if no state data is available
  const { data: visitorData, isLoading } = useQuery({
    queryKey: ['visitor', id],
    queryFn: () => getVisitorById(id!, firestoreUser ?? undefined),
    enabled: !!id && !state?.visitor && !!firestoreUser,
    staleTime: 5 * 60 * 1000,
  });

  // Use state data if available, otherwise use fetched data
  const displayVisitor = state?.visitor || visitorData;

  // Mutations
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Visitor> }) =>
      updateVisitor(id, data, firestoreUser!),
    onSuccess: () => {
      message.success(t('visitors:updateSuccess'));
      queryClient.invalidateQueries({ queryKey: ['visitor', id] });
      setIsEditModalOpen(false);
    },
    onError: () => {
      message.error(t('visitors:updateError'));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteVisitor(id, firestoreUser!),
    onSuccess: () => {
      message.success(t('visitors:deleteSuccess'));
      navigate('/residents/visitors');
    },
    onError: () => {
      message.error(t('visitors:deleteError'));
    },
  });

  // Loading state - only show if no state data is available
  if (isLoading && !state?.visitor) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!displayVisitor) {
    return <Card>{t('visitors:notFound')}</Card>;
  }

  const getStatusColor = (status: VisitorStatus) => {
    const colors: Record<VisitorStatus, string> = {
      pending: 'warning',
      approved: 'success',
      rejected: 'error',
      checked_in: 'processing',
      checked_out: 'default',
      expired: 'error',
    };
    return colors[status];
  };

  const getTypeColor = (type: VisitorType) => {
    const colors: Record<VisitorType, string> = {
      guest: 'blue',
      service: 'green',
      delivery: 'orange',
      contractor: 'purple',
      other: 'default',
    };
    return colors[type];
  };

  const handleEdit = () => {
    setIsEditModalOpen(true);
  };

  const handleDelete = () => {
    setIsDeleteModalOpen(true);
  };

  const handleEditSuccess = (data: Omit<Visitor, 'id'>) => {
    updateMutation.mutate({ id: displayVisitor.id, data });
  };

  const handleDeleteConfirm = () => {
    deleteMutation.mutate(displayVisitor.id);
  };

  // Prepare tab items
  const tabItems = [
    {
      key: 'details',
      label: t('visitors:details'),
      children: (
        <Card>
          <Descriptions bordered column={2}>
            <Descriptions.Item label={t('visitors:name')} span={2}>
              <Text strong>{displayVisitor.name}</Text>
            </Descriptions.Item>
            
            <Descriptions.Item label={t('visitors:type')}>
              <Tag color={getTypeColor(displayVisitor.type)}>
                {t(`visitors:types.${displayVisitor.type}`)}
              </Tag>
            </Descriptions.Item>
            
            <Descriptions.Item label={t('visitors:status')}>
              <Tag color={getStatusColor(displayVisitor.status)}>
                {t(`visitors:statuses.${displayVisitor.status}`)}
              </Tag>
            </Descriptions.Item>
            
            <Descriptions.Item label={t('visitors:phone')}>
              {displayVisitor.phone || '-'}
            </Descriptions.Item>
            
            <Descriptions.Item label={t('visitors:email')}>
              {displayVisitor.email || '-'}
            </Descriptions.Item>
            
            {displayVisitor.idType && (
              <Descriptions.Item label={t('visitors:idType')}>
                {t(`visitors:idTypes.${displayVisitor.idType}`)}
              </Descriptions.Item>
            )}
            
            {displayVisitor.idNumber && (
              <Descriptions.Item label={t('visitors:idNumber')}>
                {displayVisitor.idNumber}
              </Descriptions.Item>
            )}
            
            <Descriptions.Item label={t('visitors:numberOfVisitors')}>
              {displayVisitor.numberOfVisitors}
            </Descriptions.Item>
            
            <Descriptions.Item label={t('visitors:expectedArrival')}>
              {displayVisitor.expectedArrival ? dayjs(displayVisitor.expectedArrival).format('YYYY-MM-DD HH:mm') : '-'}
            </Descriptions.Item>
            
            <Descriptions.Item label={t('visitors:expectedDeparture')}>
              {displayVisitor.expectedDeparture ? dayjs(displayVisitor.expectedDeparture).format('YYYY-MM-DD HH:mm') : '-'}
            </Descriptions.Item>
            
            <Descriptions.Item label={t('visitors:actualArrival')}>
              {displayVisitor.actualArrival ? dayjs(displayVisitor.actualArrival).format('YYYY-MM-DD HH:mm') : '-'}
            </Descriptions.Item>
            
            <Descriptions.Item label={t('visitors:actualDeparture')}>
              {displayVisitor.actualDeparture ? dayjs(displayVisitor.actualDeparture).format('YYYY-MM-DD HH:mm') : '-'}
            </Descriptions.Item>
            
            <Descriptions.Item label={t('visitors:purpose')} span={2}>
              {displayVisitor.purpose}
            </Descriptions.Item>
            
            {displayVisitor.resident && (
              <Descriptions.Item label={t('visitors:resident')}>
                {displayVisitor.resident.name}
              </Descriptions.Item>
            )}
            
            {displayVisitor.employee && (
              <Descriptions.Item label={t('visitors:employee')}>
                {displayVisitor.employee.name}
              </Descriptions.Item>
            )}
            
            {displayVisitor.vehicleDetails && (
              <>
                <Descriptions.Item label={t('visitors:vehicleType')}>
                  {displayVisitor.vehicleDetails.type}
                </Descriptions.Item>
                <Descriptions.Item label={t('visitors:vehicleNumber')}>
                  {displayVisitor.vehicleDetails.number}
                </Descriptions.Item>
                <Descriptions.Item label={t('visitors:vehicleColor')}>
                  {displayVisitor.vehicleDetails.color}
                </Descriptions.Item>
              </>
            )}
            
            {displayVisitor.notes && (
              <Descriptions.Item label={t('visitors:notes')} span={2}>
                {displayVisitor.notes}
              </Descriptions.Item>
            )}
          </Descriptions>
        </Card>
      ),
    },
  ];

  // Add resident tab if available
  if (displayVisitor.resident) {
    tabItems.push({
      key: 'resident',
      label: t('visitors:resident'),
      children: <ResidentTab residentId={displayVisitor.resident.id} />,
    });
  }

  // Add employee tab if available
  if (displayVisitor.employee) {
    tabItems.push({
      key: 'employee',
      label: t('visitors:employee'),
      children: <EmployeeTab employeeId={displayVisitor.employee.id} />,
    });
  }

  // Add activity log tab
  tabItems.push({
    key: 'activity',
    label: t('visitors:activityLog'),
    children: (
      <Card>
        <ActivityLogList activityRecords={displayVisitor.activityRecords || []} />
      </Card>
    ),
  });

  return (
    <div style={{ width: '100%' }}>
      {/* Header */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Space>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
              {t('common:back')}
            </Button>
            <Title level={3}>
              {t('visitors:visitor')} - {displayVisitor.name}
            </Title>
          </Space>
        </Col>
        <Col>
          <Space>
            <Button icon={<EditOutlined />} onClick={handleEdit}>
              {t('common:actions.edit')}
            </Button>
            <Button danger icon={<DeleteOutlined />} onClick={handleDelete}>
              {t('common:actions.delete')}
            </Button>
          </Space>
        </Col>
      </Row>

      {/* Content */}
      <Tabs defaultActiveKey="details" items={tabItems} />

      {/* Modals */}
      <VisitorModal
        open={isEditModalOpen}
        visitor={displayVisitor}
        onSuccess={handleEditSuccess}
        onCancel={() => setIsEditModalOpen(false)}
      />

      <DeleteConfirmationModal
        visible={isDeleteModalOpen}
        title={t('visitors:deleteConfirmTitle')}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setIsDeleteModalOpen(false)}
      />
    </div>
  );
};

export default VisitorDetailsPage; 